import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: Record<string, unknown>;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  schemaJson,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = `${title} | 昆仑雪`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const ogTitleFinal = ogTitle || title;
    const ogDescFinal = ogDescription || description;
    setMeta('og:title', ogTitleFinal, true);
    setMeta('og:description', ogDescFinal, true);
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', '昆仑雪', true);
    setMeta('og:locale', 'zh_CN', true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitleFinal);
    setMeta('twitter:description', ogDescFinal);

    let scriptEl: HTMLScriptElement | null = null;
    if (schemaJson) {
      const existingScript = document.querySelector('script[data-schema="dynamic"]');
      if (existingScript) existingScript.remove();
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.setAttribute('data-schema', 'dynamic');
      scriptEl.textContent = JSON.stringify(schemaJson);
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogImage, schemaJson]);

  return null;
}